import { useAuth } from 'app/context/auth-context';
import { useCallback, useEffect, useRef } from 'react';

import { useIdleTimer } from './useIdleTimer';

interface AutoLogoutConfig {
  timeoutDuration?: number // in milliseconds
  warningDuration?: number // in milliseconds
  onWarning?: () => void
  onTimeout?: () => void
  onUserActive?: () => void
  enabled?: boolean
}

interface AutoLogoutHook {
  isWarning: boolean
  remainingTime: number
  extendSession: () => void
  forceLogout: () => void
  pauseTimer: () => void
  resumeTimer: () => void
}

export const useAutoLogout = (config: AutoLogoutConfig = {}): AutoLogoutHook => {
  const {
    timeoutDuration = 20 * 60 * 1000, // 20 minutes
    warningDuration = 2 * 60 * 1000, // 2 minutes warning
    onWarning,
    onTimeout,
    onUserActive,
    enabled = true
  } = config;

  const authContext = useAuth();
  const hasWarned = useRef<boolean>(false);

  const handleIdle = useCallback(() => {
    console.log('User idle detected - logging out');
    // authContext.logout()
    onTimeout?.();
  }, [authContext, onTimeout]);

  const handleActive = useCallback(() => {
    hasWarned.current = false;
    onUserActive?.();
  }, [onUserActive]);

  const handleWarning = useCallback(() => {
    if (!hasWarned.current) {
      hasWarned.current = true;
      onWarning?.();
    }
  }, [onWarning]);

  const idleTimer = useIdleTimer({
    timeout: timeoutDuration,
    warningTime: warningDuration,
    onIdle: handleIdle,
    onActive: handleActive,
    onWarning: handleWarning,
    startOnMount: enabled,
    debounceDelay: 1000
  });

  const extendSession = useCallback(() => {
    console.log('Session extended by user');
    hasWarned.current = false;
    idleTimer.reset();
  }, [idleTimer]);

  const forceLogout = useCallback(() => {
    console.log('Force logout initiated');
    idleTimer.stop();
    // authContext.logout()
    onTimeout?.();
  }, [idleTimer, authContext, onTimeout]);

  const pauseTimer = useCallback(() => {
    idleTimer.pause();
  }, [idleTimer]);

  const resumeTimer = useCallback(() => {
    idleTimer.resume();
  }, [idleTimer]);

  // Handle authentication state changes
  useEffect(() => {
    if (!authContext.user && enabled) {
      idleTimer.stop();
    } else if (authContext.user && enabled) {
      idleTimer.start();
    }
  }, [authContext.user, enabled, idleTimer]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, pause the timer to prevent logout while away
        pauseTimer();
      } else {
        // Page is visible again, resume the timer
        resumeTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [pauseTimer, resumeTimer]);

  // Handle window focus/blur
  useEffect(() => {
    const handleFocus = () => resumeTimer();
    const handleBlur = () => pauseTimer();

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [pauseTimer, resumeTimer]);

  return {
    isWarning: idleTimer.isWarning,
    remainingTime: idleTimer.remainingTime,
    extendSession,
    forceLogout,
    pauseTimer,
    resumeTimer
  };
};
